import { parseClientReplyAs, parseClientReplyUnion, parseVerbProgramCompileOutcome } from "@moor/web-sdk";
import { MoorVar } from "@moor/web-sdk";
import { PropertyValue } from "@moor/schema/generated/moor-rpc/property-value";
import { VerbValue } from "@moor/schema/generated/moor-rpc/verb-value";
import { Var as FbVar } from "@moor/schema/generated/moor-var/var";
import { VarUnion } from "@moor/schema/generated/moor-var/var-union";
import { VarStr } from "@moor/schema/generated/moor-var/var-str";
import { VarList } from "@moor/schema/generated/moor-var/var-list";
import { VarSym } from "@moor/schema/generated/moor-var/var-sym";
import { Symbol as FbSymbol } from "@moor/schema/generated/moor-common/symbol";
import * as flatbuffers from "flatbuffers";

export type EditorTarget = "verb-editor" | "property-value-editor" | "text-editor";

export interface EditorSession {
    presentationId: string;
    target: EditorTarget;
    /** For verb-editor / property-value-editor: the object being edited.
     *  For text-editor: the callback object to invoke on commit. */
    objectCurie: string;
    /** Verb name (verb-editor), property name (property-value-editor),
     *  or callback verb name (text-editor). */
    itemName: string;
    title: string;
    /** Initial content — pre-populated for text-editor, fetched for verb/property. */
    content: string;
    contentType: "text/plain" | "text/djot";
    /** "string" or "list" — how text-editor content is sent to the callback verb. */
    textMode: "string" | "list";
    /** session_id for text-editor sessions; also the presentationId for those. */
    sessionId?: string;
}

type CommitStatus = "idle" | "saving" | "error" | "ok";
/** How to re-encode property content on commit. */
type PropKind = "string" | "list" | "literal";

class EditorStore {
    session = $state<EditorSession | null>(null);
    status = $state<CommitStatus>("idle");
    statusMessage = $state<string>("");
    /** Remembered for property-value-editor commit encoding. */
    private propKind: PropKind = "literal";

    get active() { return this.session !== null; }

    openFromPresentation(
        presentationId: string,
        target: string,
        content: string,
        contentType: string,
        attributes: Array<[string, string]>,
    ) {
        const attr = Object.fromEntries(attributes);
        const knownTargets: EditorTarget[] = ["verb-editor", "property-value-editor", "text-editor"];
        if (!knownTargets.includes(target as EditorTarget)) {
            console.debug("[editor] ignoring present event for unknown target:", target, "id:", presentationId, "attrs:", attributes);
            return;
        }

        const session: EditorSession = {
            presentationId,
            target: target as EditorTarget,
            objectCurie: attr["object"] ?? "",
            itemName: attr["verb"] ?? attr["property"] ?? "",
            title: attr["title"] ?? target,
            content,
            contentType: (contentType as "text/plain" | "text/djot") ?? "text/plain",
            textMode: (attr["text_mode"] as "string" | "list") ?? "string",
            sessionId: attr["session_id"],
        };
        console.debug("[editor] opening session:", session);
        this.session = session;
        this.status = "idle";
        this.statusMessage = "";
    }

    close() {
        this.session = null;
        this.status = "idle";
        this.statusMessage = "";
    }

    async loadInitialContent(host: string, authToken: string): Promise<string> {
        const s = this.session;
        if (!s) return "";

        if (s.target === "text-editor") return s.content;

        try {
            const headers = { "X-Moor-Auth-Token": authToken };
            const curie = encodeURIComponent(s.objectCurie);

            if (s.target === "verb-editor") {
                const url = `${host}/v1/verbs/${curie}/${encodeURIComponent(s.itemName)}`;
                console.debug("[editor] fetching verb:", url);
                const resp = await fetch(url, { headers });
                if (!resp.ok) {
                    const msg = await resp.text().catch(() => resp.statusText);
                    throw new Error(`HTTP ${resp.status}: ${msg}`);
                }
                const bytes = new Uint8Array(await resp.arrayBuffer());
                const vv = parseClientReplyAs(bytes, "load verb", VerbValue);
                const lines: string[] = [];
                for (let i = 0; i < vv.codeLength(); i++) lines.push(vv.code(i) as string);
                return lines.join("\n");
            }

            if (s.target === "property-value-editor") {
                const url = `${host}/v1/properties/${curie}/${encodeURIComponent(s.itemName)}`;
                console.debug("[editor] fetching property:", url);
                const resp = await fetch(url, { headers });
                if (!resp.ok) {
                    const msg = await resp.text().catch(() => resp.statusText);
                    throw new Error(`HTTP ${resp.status}: ${msg}`);
                }
                const bytes = new Uint8Array(await resp.arrayBuffer());
                const pv = parseClientReplyAs(bytes, "load property", PropertyValue);
                const v = pv.value();
                if (!v) return "";
                const mv = new MoorVar(v as never);
                const list = mv.asList();
                if (list !== null) {
                    this.propKind = "list";
                    return list.map(item => item.asString() ?? item.toLiteral()).join("\n");
                }
                const str = mv.asString();
                if (str !== null) {
                    this.propKind = "string";
                    return str;
                }
                this.propKind = "literal";
                return mv.toLiteral();
            }
        } catch (e) {
            const msg = e instanceof Error ? e.message : String(e);
            console.error("[editor] loadInitialContent failed:", msg);
            this.status = "error";
            this.statusMessage = `Load failed: ${msg}`;
        }
        return "";
    }

    async commit(content: string, host: string, authToken: string) {
        const s = this.session;
        if (!s) return;

        this.status = "saving";
        this.statusMessage = "";

        try {
            const headers: Record<string, string> = { "X-Moor-Auth-Token": authToken };
            const curie = encodeURIComponent(s.objectCurie);

            if (s.target === "verb-editor") {
                const resp = await fetch(
                    `${host}/v1/verbs/${curie}/${encodeURIComponent(s.itemName)}`,
                    { method: "POST", headers: { ...headers, "Content-Type": "text/plain" }, body: content },
                );
                if (!resp.ok) {
                    const txt = await resp.text().catch(() => resp.statusText);
                    throw new Error(txt || `HTTP ${resp.status}`);
                }
                // HTTP 200 doesn't mean compile success — parse the FlatBuffer body.
                const bytes = new Uint8Array(await resp.arrayBuffer());
                const replyUnion = parseClientReplyUnion(bytes, "verb program");
                const outcome = parseVerbProgramCompileOutcome(replyUnion, "verb program");
                if (!outcome.success) {
                    console.error("[editor] verb program error:", outcome.error);
                    throw new Error(describeVerbProgramError(outcome.error));
                }
            } else if (s.target === "property-value-editor") {
                const body = encodePropValue(content, this.propKind);
                const resp = await fetch(
                    `${host}/v1/properties/${curie}/${encodeURIComponent(s.itemName)}`,
                    { method: "POST", headers: { ...headers, "Content-Type": "text/plain" }, body },
                );
                if (!resp.ok) {
                    const txt = await resp.text().catch(() => resp.statusText);
                    throw new Error(txt || `HTTP ${resp.status}`);
                }
            } else if (s.target === "text-editor") {
                const args = buildTextEditorArgs(s.sessionId, content, s.textMode);
                const resp = await fetch(
                    `${host}/v1/verbs/${curie}/${encodeURIComponent(s.itemName)}/invoke`,
                    {
                        method: "POST",
                        headers: { ...headers, "Content-Type": "application/x-flatbuffers" },
                        body: args.buffer as ArrayBuffer,
                    },
                );
                if (!resp.ok) {
                    const txt = await resp.text().catch(() => resp.statusText);
                    throw new Error(txt || `HTTP ${resp.status}`);
                }
            }

            this.status = "ok";
            this.statusMessage = "Saved.";
            setTimeout(() => { this.session = null; this.status = "idle"; }, 800);
        } catch (e) {
            this.status = "error";
            this.statusMessage = e instanceof Error ? e.message : String(e);
        }
    }

    async abort(host: string, authToken: string) {
        const s = this.session;
        if (!s) return;

        const sessionId = s.sessionId;
        const curie = encodeURIComponent(s.objectCurie);
        const doClose = async () => {
            try {
                await fetch(`${host}/v1/presentations/${encodeURIComponent(s.presentationId)}`, {
                    method: "DELETE",
                    headers: { "X-Moor-Auth-Token": authToken },
                });
            } catch { /* best-effort */ }
        };

        if (s.target === "text-editor" && sessionId) {
            try {
                const args = buildTextEditorCloseArgs(sessionId);
                await fetch(
                    `${host}/v1/verbs/${curie}/${encodeURIComponent(s.itemName)}/invoke`,
                    {
                        method: "POST",
                        headers: {
                            "X-Moor-Auth-Token": authToken,
                            "Content-Type": "application/x-flatbuffers",
                        },
                        body: args.buffer as ArrayBuffer,
                    },
                );
            } catch { /* best-effort */ }
        }

        await doClose();
        this.session = null;
        this.status = "idle";
        this.statusMessage = "";
    }
}

export const editor = new EditorStore();

// ── Error formatting ──────────────────────────────────────────────────────

function describeVerbProgramError(err: { type: unknown; message: string; compileError?: unknown }): string {
    // Try to extract line/message from ParseError via duck-typing (avoids importing all union types).
    const ce = err.compileError as any;
    if (ce) {
        // ParseError / StringLexError carry errorPosition().line() and message()
        const inner = ce.error?.({ bb: ce.bb, bb_pos: 0, __init(i: number, bb: unknown) { (this as any).bb = bb; (this as any).bb_pos = i; return this; } });
        if (inner) {
            const msg: string | null = inner.message?.() ?? null;
            const line: bigint | null = inner.errorPosition?.()?.line?.() ?? null;
            const col: bigint | null = inner.errorPosition?.()?.col?.() ?? null;
            if (msg && line != null) return `Line ${line}:${col ?? 0} — ${msg}`;
            if (msg) return msg;
        }
    }
    return err.message || "Compile error";
}

// ── Property value encoding ────────────────────────────────────────────────

function mooEscStr(s: string): string {
    return s.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
}

function encodePropValue(content: string, kind: PropKind): string {
    if (kind === "list") {
        const items = content.split("\n").map(l => `"${mooEscStr(l)}"`);
        return `{${items.join(", ")}}`;
    }
    if (kind === "string") {
        return `"${mooEscStr(content)}"`;
    }
    return content; // literal — user edited MOO syntax directly
}

// ── FlatBuffer arg builders ────────────────────────────────────────────────

function buildTextEditorArgs(
    sessionId: string | undefined,
    content: string,
    textMode: "string" | "list",
): Uint8Array {
    const estimatedSize = 512 + content.length * 2;
    const builder = new flatbuffers.Builder(estimatedSize);

    let contentVarOff: number;
    if (textMode === "list") {
        const lines = content.split("\n");
        const lineOffsets: number[] = [];
        for (const line of lines) {
            const strOff = builder.createString(line);
            const vsOff = VarStr.createVarStr(builder, strOff);
            lineOffsets.push(FbVar.createVar(builder, VarUnion.VarStr, vsOff));
        }
        const elemsOff = VarList.createElementsVector(builder, lineOffsets);
        const listOff = VarList.createVarList(builder, elemsOff);
        contentVarOff = FbVar.createVar(builder, VarUnion.VarList, listOff);
    } else {
        const strOff = builder.createString(content);
        const vsOff = VarStr.createVarStr(builder, strOff);
        contentVarOff = FbVar.createVar(builder, VarUnion.VarStr, vsOff);
    }

    const outerOffsets: number[] = [];
    if (sessionId) {
        const sOff = builder.createString(sessionId);
        const svOff = VarStr.createVarStr(builder, sOff);
        outerOffsets.push(FbVar.createVar(builder, VarUnion.VarStr, svOff));
    }
    outerOffsets.push(contentVarOff);

    const elemsOff = VarList.createElementsVector(builder, outerOffsets);
    const listOff = VarList.createVarList(builder, elemsOff);
    const rootOff = FbVar.createVar(builder, VarUnion.VarList, listOff);

    builder.finish(rootOff);
    return builder.asUint8Array();
}

function buildTextEditorCloseArgs(sessionId: string): Uint8Array {
    const builder = new flatbuffers.Builder(256);

    const sOff = builder.createString(sessionId);
    const svOff = VarStr.createVarStr(builder, sOff);
    const sessionVarOff = FbVar.createVar(builder, VarUnion.VarStr, svOff);

    const cOff = builder.createString("close");
    const symOff = FbSymbol.createSymbol(builder, cOff);
    const vsymOff = VarSym.createVarSym(builder, symOff);
    const closeVarOff = FbVar.createVar(builder, VarUnion.VarSym, vsymOff);

    const elemsOff = VarList.createElementsVector(builder, [sessionVarOff, closeVarOff]);
    const listOff = VarList.createVarList(builder, elemsOff);
    const rootOff = FbVar.createVar(builder, VarUnion.VarList, listOff);

    builder.finish(rootOff);
    return builder.asUint8Array();
}
